import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Tooltip } from '@mantine/core';

import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { Divider } from 'ui-components/components/divider';

import { useUserHasConfigurationRole } from '../../../hooks/use-user-role';
import { ModalPortal } from '../../modal-portal';
import { getProviderDisplayName } from '../../../pages/settings/carriers/freight/provider-display-name';
import {
  UNISHIPPERS_API_CODE,
  UNISHIPPERS_DISPLAY_NAME,
  WWEX_API_CODE,
  WWEX_DISPLAY_NAME,
} from '../../../pages/settings/carriers/freight/constants';

import wwexIcon from '../../../svgs/wwex-icon.svg';
import unishippersIcon from '../../../svgs/unishippers-icon.svg';
import { messages } from './messages';
import style from './style.module.css';

type FreightAccountActivationModalProps = {
  onClose: () => void;
  onActivate: () => void;
  onConnectWwex: () => void;
  onConnectUnishippers: () => void;
  shouldShowActivateOption?: boolean;
};

export const FreightAccountActivationModal = ({
  onClose,
  onActivate,
  onConnectWwex,
  onConnectUnishippers,
  shouldShowActivateOption = true,
}: FreightAccountActivationModalProps) => {
  const intl = useIntl();
  const hasConfigurationRole = useUserHasConfigurationRole();
  const disabledTooltip = hasConfigurationRole ? undefined : intl.formatMessage(messages.configurationDisabledTooltip);
  const wwexDisplayName = getProviderDisplayName(WWEX_DISPLAY_NAME, WWEX_API_CODE);
  const unishippersDisplayName = getProviderDisplayName(UNISHIPPERS_DISPLAY_NAME, UNISHIPPERS_API_CODE);

  return (
    <ModalPortal onClose={onClose}>
      <Modal className={style.modal}>
        <ModalHeader closeModal={onClose}>
          <FormattedMessage {...(shouldShowActivateOption ? messages.modalTitle : messages.connectOnlyModalTitle)} />
        </ModalHeader>
        <ModalBody className={style['modal-body']}>
          <FlexColumn gap={Gap.Space16}>
            {shouldShowActivateOption && (
              <>
                <FlexColumn gap={Gap.Space12}>
                  <Text textType={TextType.H3} value={messages.activateHeading} />
                  <Text textType={TextType.Paragraph} value={messages.activateDescription} />
                  <Tooltip disabled={!disabledTooltip} label={disabledTooltip ?? ''} position='top'>
                    <Box component='span' display='inline-block'>
                      <Button
                        className={style['activate-button']}
                        disabled={!hasConfigurationRole}
                        onClick={onActivate}
                        theme={THEME_CONSTANTS.primary}
                      >
                        <FormattedMessage {...messages.activateButton} />
                      </Button>
                    </Box>
                  </Tooltip>
                </FlexColumn>

                <FlexRow className={style['or-divider-row']} gap={Gap.Space12}>
                  <Divider className={style['or-divider-line']} />
                  <Text className={style['or-divider-text']} textType={TextType.Description} value={messages.orDivider} />
                  <Divider className={style['or-divider-line']} />
                </FlexRow>
              </>
            )}

            <FlexColumn gap={Gap.Space12}>
              <Text textType={TextType.H3} value={shouldShowActivateOption ? messages.connectHeading : messages.connectOnlyHeading} />
              <FlexRow className={style['connect-row']} gap={Gap.Space12}>
                <img alt={wwexDisplayName} className={style['provider-logo']} src={wwexIcon} />
                <Text className={style['provider-name']} textType={TextType.Paragraph} value={wwexDisplayName} />
                <Tooltip disabled={!disabledTooltip} label={disabledTooltip ?? ''} position='top'>
                  <Box component='span' display='inline-block'>
                    <Button
                      className={style['connect-button']}
                      disabled={!hasConfigurationRole}
                      onClick={onConnectWwex}
                      theme={THEME_CONSTANTS.secondary}
                    >
                      <FormattedMessage {...messages.connectButton} />
                    </Button>
                  </Box>
                </Tooltip>
              </FlexRow>
              <FlexRow className={style['connect-row']} gap={Gap.Space12}>
                <img alt={unishippersDisplayName} className={style['provider-logo']} src={unishippersIcon} />
                <Text className={style['provider-name']} textType={TextType.Paragraph} value={unishippersDisplayName} />
                <Tooltip disabled={!disabledTooltip} label={disabledTooltip ?? ''} position='top'>
                  <Box component='span' display='inline-block'>
                    <Button
                      className={style['connect-button']}
                      disabled={!hasConfigurationRole}
                      onClick={onConnectUnishippers}
                      theme={THEME_CONSTANTS.secondary}
                    >
                      <FormattedMessage {...messages.connectButton} />
                    </Button>
                  </Box>
                </Tooltip>
              </FlexRow>
            </FlexColumn>
          </FlexColumn>
        </ModalBody>
      </Modal>
    </ModalPortal>
  );
};
